import React, { useState, useEffect } from 'react';

/**
 * CheckboxListRenderer - Renders a list of checkboxes for multi-select fields
 * This provides better UX than native HTML multi-select which requires Ctrl/Cmd+click
 * for non-contiguous selections.
 * 
 * For "Other (specify)" freetext, the custom text is stored as "other:Custom text here"
 * in the values array.
 */
const CheckboxListRenderer = ({ field, value, onChange, error, readOnly }) => {
  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
  
  // Extract "other" text from values if present (stored as "other:text")
  const otherEntry = selectedValues.find(v => typeof v === 'string' && v.startsWith('other:'));
  const initialOtherText = otherEntry ? otherEntry.substring(6) : '';
  const [otherText, setOtherText] = useState(initialOtherText);
  
  // Check if "other" is selected (either as "other" or "other:text")
  const isOtherSelected = selectedValues.some(v => v === 'other' || (typeof v === 'string' && v.startsWith('other:')));
  
  // Sync otherText state when value prop changes (e.g., form reset)
  useEffect(() => {
    const values = Array.isArray(value) ? value : (value ? [value] : []);
    const entry = values.find(v => typeof v === 'string' && v.startsWith('other:'));
    const hasOther = values.some(v => v === 'other' || (typeof v === 'string' && v.startsWith('other:')));
    if (entry) {
      setOtherText(entry.substring(6));
    } else if (!hasOther) {
      setOtherText('');
    }
  }, [value]);
  
  const handleCheckboxChange = (option, checked) => {
    if (readOnly) return;
    
    let newValues;
    if (option === 'other') {
      if (checked) {
        // Add "other" - will be converted to "other:text" when text is entered
        newValues = [...selectedValues.filter(v => !v.startsWith?.('other')), 'other'];
      } else {
        // Remove any "other" or "other:text" entries
        newValues = selectedValues.filter(v => v !== 'other' && !(typeof v === 'string' && v.startsWith('other:')));
      }
    } else {
      if (checked) {
        newValues = [...selectedValues, option];
      } else {
        newValues = selectedValues.filter(v => v !== option);
      }
    }
    onChange(newValues);
  };

  const showFreetext = (field.type === 'multiselect_with_freetext') && isOtherSelected;

  const handleOtherTextChange = (e) => {
    const text = e.target.value;
    setOtherText(text);
    
    // Update the values array: replace "other" or "other:oldtext" with "other:newtext"
    const newValues = selectedValues.filter(v => v !== 'other' && !(typeof v === 'string' && v.startsWith('other:')));
    if (text) {
      newValues.push(`other:${text}`);
    } else {
      newValues.push('other');
    }
    onChange(newValues);
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        <span className="text-gray-500 font-normal ml-1">(select all that apply)</span>
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {field.help_text && (
        <p className="text-xs text-gray-500 mb-2">{field.help_text}</p>
      )}
      
      <div className={`border rounded-md p-3 ${error ? 'border-red-500' : 'border-gray-300'} ${readOnly ? 'bg-gray-100' : 'bg-white'}`}>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {field.options && field.options.map((option, index) => (
            <label 
              key={index} 
              className={`flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded ${readOnly ? 'cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                disabled={readOnly}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">{option}</span>
            </label>
          ))}
          
          {field.type === 'multiselect_with_freetext' && (
            <label 
              className={`flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded ${readOnly ? 'cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes('other')}
                onChange={(e) => handleCheckboxChange('other', e.target.checked)}
                disabled={readOnly}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Other (specify)</span>
            </label>
          )}
        </div>
      </div>
      
      {showFreetext && (
        <input
          type="text"
          value={otherText}
          onChange={handleOtherTextChange}
          placeholder="Please specify..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={readOnly}
        />
      )}
      
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default CheckboxListRenderer;
